"use client"

import { useIsDesktop } from "@/lib/common/MediaQueries"
import { useCallback, useEffect, useRef, useState } from "react"
import {
  NEWSLETTER_ENTRY_DELAY_DESKTOP_MS,
  NEWSLETTER_ENTRY_DELAY_MS,
  NEWSLETTER_EXIT_INACTIVITY_MS,
  NEWSLETTER_SOURCE_ENTRY_POPUP,
  NEWSLETTER_SOURCE_EXIT_POPUP,
} from "./newsletterContent"
import { isPopupSuppressed, markPopupShown } from "./newsletterSuppression"

interface NewsletterPopupState {
  isOpen: boolean
  source: string
  close: () => void
}

// Entry popup on both platforms (DLK-1027; was mobile-only) — immediately on desktop, after a
// delay on mobile. The exit trigger is platform-specific: pointer leaving the top of the
// viewport on desktop, idle time on mobile. Whichever fires first wins, since only one popup
// shows per day — so on desktop the entry popup effectively always takes the slot.
export const useNewsletterPopup = (): NewsletterPopupState => {
  const isDesktop = useIsDesktop()
  const [isOpen, setIsOpen] = useState(false)
  const [source, setSource] = useState<string>(NEWSLETTER_SOURCE_EXIT_POPUP)
  const openedRef = useRef(false)

  const open = useCallback((src: string): void => {
    if (openedRef.current || isPopupSuppressed()) return
    openedRef.current = true
    markPopupShown()
    setSource(src)
    setIsOpen(true)
  }, [])

  const close = useCallback((): void => setIsOpen(false), [])

  // Entry popup. Separate from the exit effect so the mobile delay is driven only by the
  // breakpoint, not by the exit listeners. `useIsDesktop` reports false on the first render and
  // resolves on mount, so on desktop the mobile delay is armed and then replaced by the
  // zero-delay pass once the media query lands.
  useEffect(() => {
    if (isPopupSuppressed()) return undefined

    const entryTimer = window.setTimeout(
      () => open(NEWSLETTER_SOURCE_ENTRY_POPUP),
      isDesktop ? NEWSLETTER_ENTRY_DELAY_DESKTOP_MS : NEWSLETTER_ENTRY_DELAY_MS,
    )
    return () => window.clearTimeout(entryTimer)
  }, [isDesktop, open])

  // Exit popup.
  useEffect(() => {
    if (isPopupSuppressed()) return undefined

    if (isDesktop) {
      const onMouseOut = (e: MouseEvent): void => {
        if (!e.relatedTarget && e.clientY <= 0) open(NEWSLETTER_SOURCE_EXIT_POPUP)
      }
      document.addEventListener("mouseout", onMouseOut)
      return () => document.removeEventListener("mouseout", onMouseOut)
    }

    let idleTimer = window.setTimeout(
      () => open(NEWSLETTER_SOURCE_EXIT_POPUP),
      NEWSLETTER_EXIT_INACTIVITY_MS,
    )
    const resetIdle = (): void => {
      window.clearTimeout(idleTimer)
      idleTimer = window.setTimeout(
        () => open(NEWSLETTER_SOURCE_EXIT_POPUP),
        NEWSLETTER_EXIT_INACTIVITY_MS,
      )
    }
    const events: (keyof WindowEventMap)[] = ["touchstart", "scroll", "keydown", "click"]
    events.forEach((ev) => window.addEventListener(ev, resetIdle, { passive: true }))
    return () => {
      window.clearTimeout(idleTimer)
      events.forEach((ev) => window.removeEventListener(ev, resetIdle))
    }
  }, [isDesktop, open])

  return { isOpen, source, close }
}
